import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SingleDayEvent } from '../utils/careHomeCardFormatting';
import api from '../services/api';
import { buildIcsContent, downloadIcs, makeIcsFilename } from '../utils/icsGenerator';

interface EventPopupProps {
    events: SingleDayEvent[];
    careHomeName: string;
    // update 2026_04.22.4 - needed to fetch address + log add_to_calendar
    regulatorId: string;
    userId?: string;
    startingPostCode?: string;
    searchPosition?: number;
    onClose: () => void;
}

// One stable browser_session value per tab, matching how /api/search uses it.
const getBrowserSession = (): string => {
    try {
        const existing = sessionStorage.getItem('browser_session');
        if (existing) return existing;
        const fresh = 'web_session_' + Date.now();
        sessionStorage.setItem('browser_session', fresh);
        return fresh;
    } catch {
        return 'web_session_' + Date.now();
    }
};

// Desktop-only post-download hint. iPadOS masquerades as Mac on Safari, so we
// exclude touch devices via maxTouchPoints. (update 2026_04.22.4)
const isWindowsOrMac = (): boolean => {
    if (typeof navigator === 'undefined') return false;
    if (navigator.maxTouchPoints && navigator.maxTouchPoints > 1) return false;
    const navAny = navigator as Navigator & { userAgentData?: { platform?: string } };
    const p = (
        navAny.userAgentData?.platform ||
        navigator.platform ||
        navigator.userAgent ||
        ''
    ).toLowerCase();
    return p.includes('win') || p.includes('mac');
};

const EventPopup: React.FC<EventPopupProps> = ({
    events,
    careHomeName,
    regulatorId,
    userId = '',
    startingPostCode = '',
    searchPosition = 0,
    onClose
}) => {
    const [busyIndex, setBusyIndex] = useState<number | null>(null);
    const [errorIndex, setErrorIndex] = useState<number | null>(null);
    // Post-download hint: index of the event whose hint is visible, and filename to reference.
    const [hintIndex, setHintIndex] = useState<number | null>(null);
    const [hintFilename, setHintFilename] = useState<string>('');
    const hintTimerRef = useRef<number | null>(null);

    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    // Clear any pending hint timer on unmount so we don't setState on a dead tree.
    useEffect(() => () => {
        if (hintTimerRef.current !== null) {
            window.clearTimeout(hintTimerRef.current);
            hintTimerRef.current = null;
        }
    }, []);

    const handleAddToCalendar = async (ev: SingleDayEvent, index: number) => {
        if (busyIndex !== null) return;
        setBusyIndex(index);
        setErrorIndex(null);
        try {
            const info = await api.fetchEventCalendarInfo({
                regulator_id: regulatorId,
                event_id: ev.event_id || '',
                user_id: userId || 'anonymous_' + Date.now(),
                browser_session: getBrowserSession(),
                starting_post_code: startingPostCode,
                search_position: searchPosition
            });

            const ics = buildIcsContent(
                {
                    event_id: ev.event_id,
                    event_name: ev.event_name,
                    event_description: ev.event_description,
                    datetime_start: ev.datetime_start,
                    datetime_end: ev.datetime_end,
                    contact_name: ev.contact_name,
                    contact_phone: ev.contact_phone,
                    contact_email: ev.contact_email
                },
                {
                    carehome_name: info.carehome_name || careHomeName,
                    address_1: info.address_1,
                    address_2: info.address_2,
                    town: info.town,
                    county: info.county,
                    post_code: info.post_code,
                    latitude: info.latitude,
                    longitude: info.longitude,
                    regulator_id: regulatorId
                }
            );

            const filename = makeIcsFilename(ev.event_name);
            downloadIcs(filename, ics);

            // Desktop-only post-download hint for 5 seconds (update 2026_04.22.4).
            if (isWindowsOrMac()) {
                if (hintTimerRef.current !== null) {
                    window.clearTimeout(hintTimerRef.current);
                }
                setHintFilename(filename);
                setHintIndex(index);
                hintTimerRef.current = window.setTimeout(() => {
                    setHintIndex(null);
                    hintTimerRef.current = null;
                }, 5000);
            }
        } catch (err) {
            console.error('Add to calendar failed', err);
            setErrorIndex(index);
        } finally {
            setBusyIndex(null);
        }
    };

    const overlayStyle: React.CSSProperties = {
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.45)',
        padding: '1rem',
    };

    const panelStyle: React.CSSProperties = {
        position: 'relative',
        width: '100%',
        maxWidth: '420px',
        maxHeight: '80vh',
        borderRadius: '16px',
        background: '#ffffff',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2)',
        fontFamily: '"Trebuchet MS", sans-serif',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    };

    const headerStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.8rem 1rem',
        background: 'linear-gradient(135deg, #025150 0%, #0a7f72 100%)',
        color: '#ffffff',
    };

    const closeBtnStyle: React.CSSProperties = {
        background: 'none',
        border: 'none',
        color: '#ffffff',
        fontSize: '1.4rem',
        cursor: 'pointer',
        lineHeight: 1,
        padding: '0 0.2rem',
        opacity: 0.85,
    };

    const listStyle: React.CSSProperties = {
        overflowY: 'auto',
        padding: '0.6rem 0.8rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.7rem',
    };

    const cardStyle: React.CSSProperties = {
        borderRadius: '12px',
        border: '1px solid rgba(2, 81, 80, 0.12)',
        background: '#f4f3f1',
        padding: '0.7rem 0.8rem',
    };

    const nameStyle: React.CSSProperties = {
        margin: 0,
        fontSize: '0.92rem',
        fontWeight: 700,
        color: '#025150',
        lineHeight: 1.2,
    };

    const descStyle: React.CSSProperties = {
        margin: '0.24rem 0 0',
        fontSize: '0.76rem',
        color: '#4f5960',
        lineHeight: 1.3,
    };

    const timeStyle: React.CSSProperties = {
        margin: '0.4rem 0 0',
        fontSize: '0.78rem',
        fontWeight: 600,
        color: '#0d5f57',
    };

    const contactStyle: React.CSSProperties = {
        marginTop: '0.4rem',
        paddingTop: '0.36rem',
        borderTop: '1px solid #e0e2e4',
        fontSize: '0.72rem',
        color: '#4f5960',
        lineHeight: 1.45,
    };

    const linkStyle: React.CSSProperties = {
        color: '#025150',
        textDecoration: 'none',
        fontWeight: 600,
    };

    const actionsRowStyle: React.CSSProperties = {
        marginTop: '0.5rem',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '0.5rem',
    };

    const addBtnStyle: React.CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem',
        minHeight: '36px',
        padding: '0.3rem 0.7rem',
        background: '#ffffff',
        color: '#025150',
        border: '1px solid #025150',
        borderRadius: '999px',
        fontSize: '0.78rem',
        fontWeight: 600,
        fontFamily: '"Trebuchet MS", sans-serif',
        cursor: 'pointer',
        lineHeight: 1,
    };

    const addBtnBusyStyle: React.CSSProperties = {
        ...addBtnStyle,
        opacity: 0.6,
        cursor: 'wait',
    };

    const errorTextStyle: React.CSSProperties = {
        marginTop: '0.35rem',
        fontSize: '0.7rem',
        color: '#c1393a',
        textAlign: 'right',
    };

    const hintStyle: React.CSSProperties = {
        marginTop: '0.35rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '0.3rem',
        fontSize: '0.7rem',
        color: '#4f5960',
        lineHeight: 1.3,
    };

    const hintFilenameStyle: React.CSSProperties = {
        fontWeight: 600,
        color: '#025150',
    };

    const formatPhone = (phone: string): string => {
        if (!phone) return '';
        const digits = phone.replace(/\D/g, '');
        if (digits.startsWith('44') && digits.length > 2) return `+${digits.slice(0, 2)} ${digits.slice(2)}`;
        return phone;
    };

    return createPortal(
        <div style={overlayStyle} onClick={onClose}>
            <div style={panelStyle} onClick={e => e.stopPropagation()}>
                <div style={headerStyle}>
                    <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>
                        Events &mdash; {careHomeName}
                    </span>
                    <button style={closeBtnStyle} onClick={onClose} aria-label="Close">&times;</button>
                </div>

                <div style={listStyle}>
                    {events.map((ev, i) => (
                        <div key={i} style={cardStyle}>
                            <p style={nameStyle}>{ev.event_name}</p>
                            {ev.event_description && (
                                <p style={descStyle}>{ev.event_description}</p>
                            )}
                            <p style={timeStyle}>
                                {ev.formattedDate} &middot; {ev.formattedStartTime} &ndash; {ev.formattedEndTime}
                            </p>

                            {(ev.contact_name || ev.contact_phone || ev.contact_email) && (
                                <div style={contactStyle}>
                                    {ev.contact_name && <div>{ev.contact_name}</div>}
                                    {ev.contact_phone && (
                                        <div>
                                            <a href={`tel:+${ev.contact_phone.replace(/\D/g, '')}`} style={linkStyle}>
                                                {formatPhone(ev.contact_phone)}
                                            </a>
                                        </div>
                                    )}
                                    {ev.contact_email && (
                                        <div>
                                            <a href={`mailto:${ev.contact_email}`} style={linkStyle}>
                                                {ev.contact_email}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Hide Add to calendar if the care home has no reg_id - the backend
                                lookup would fail Joi validation. (update 2026_04.22.4) */}
                            {regulatorId && (
                                <div style={actionsRowStyle}>
                                    <button
                                        type="button"
                                        style={busyIndex === i ? addBtnBusyStyle : addBtnStyle}
                                        onClick={() => handleAddToCalendar(ev, i)}
                                        disabled={busyIndex !== null}
                                        aria-label={`Add ${ev.event_name} to calendar`}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                            <line x1="16" y1="2" x2="16" y2="6" />
                                            <line x1="8" y1="2" x2="8" y2="6" />
                                            <line x1="3" y1="10" x2="21" y2="10" />
                                            <line x1="12" y1="14" x2="12" y2="18" />
                                            <line x1="10" y1="16" x2="14" y2="16" />
                                        </svg>
                                        {busyIndex === i ? 'Adding…' : 'Add to calendar'}
                                    </button>
                                </div>
                            )}
                            {errorIndex === i && (
                                <div style={errorTextStyle}>
                                    Couldn&apos;t add to calendar. Please try again.
                                </div>
                            )}
                            {hintIndex === i && (
                                <div style={hintStyle} role="status" aria-live="polite">
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="7 10 12 15 17 10" />
                                        <line x1="12" y1="15" x2="12" y2="3" />
                                    </svg>
                                    <span>
                                        <span style={hintFilenameStyle}>{hintFilename}</span> downloaded. Open it to add to Outlook / Apple Calendar.
                                    </span>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default EventPopup;
